/**
 * @file Component to navigate through time
 * @author Wim Haanstra
 */
import { OnDestroy } from '@angular/core';
import * as moment from 'moment';
import { FormGroup, FormControl } from '@angular/forms';
import { LocalisationService } from '../../core/localisation.service';
import { detailLevels } from '../helpers/detail-levels';
import { ControlAdapter } from '../../base/helpers/control-adapter';
import { GridService } from '../grid.service';
import { SubscriptionPool } from '../../core/helpers/subscription-pool';
/**
 * Component to specify a horizon for views
 */
var HorizonComponent = /** @class */ (function () {
    /**
     * Creates an instance of HorizonComponent.
     * @param alertsService Service to communicate messages to the user.
     * @param {LocalisationService} localisationService
     * @param {Router} router
     *
     */
    function HorizonComponent(localisationService, gridService) {
        var _this = this;
        this.localisationService = localisationService;
        this.gridService = gridService;
        /** Keep track of the subscriptions */
        this.subscriptionPool = new SubscriptionPool();
        /** The angular FormGroup used for property validation and feedback. */
        this.form = null;
        /**
         * The horizon has support for two modes:
         * - list, with support for date and level
         * - grid, with support for date, level, office, weekends and zoom
         *
         * @type {HorizonMode}
         */
        this.mode = 'horizonModeList';
        this.overlayMode = false;
        /** Loading state of component */
        this.isLoading = false;
        /** Default the zoom out button is not visible */
        this.zoomOutVisible = false;
        this.form = new FormGroup({});
        this.form.addControl('CurrentDate', new FormControl());
        this.form.addControl('OfficeHours1', new ControlAdapter(this, 'officeHours'));
        this.form.addControl('OfficeHours2', new ControlAdapter(this, 'officeHours'));
        this.form.addControl('Weekends1', new ControlAdapter(this, 'weekends'));
        this.form.addControl('Weekends2', new ControlAdapter(this, 'weekends'));
        // Fix: Periodeniveau-kiezer past zich niet aan als periodeniveau buiten de kiezer om veranderd is. #3387
        this.form.addControl('Level', new FormControl(this.gridService.level));
        this.subscriptionPool.add(gridService.stateChanged.subscribe(function (value) {
            _this.form.controls['Level'].setValue(_this.gridService.level);
        }));
        this.subscriptionPool.add(this.form.controls['Level'].valueChanges.subscribe(function (value) {
            _this.level = value;
        }));
    }
    Object.defineProperty(HorizonComponent.prototype, "settingsOpen", {
        /** Settings open on small devices */
        get: function () {
            return this.gridService.settingsOpen;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HorizonComponent.prototype, "date", {
        get: function () {
            return this.gridService.date;
        },
        set: function (value) {
            this.gridService.date = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HorizonComponent.prototype, "level", {
        get: function () {
            return this.gridService.level;
        },
        set: function (value) {
            this.gridService.level = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HorizonComponent.prototype, "availableZoom", {
        get: function () {
            return this.gridService.availableZoom;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HorizonComponent.prototype, "zoomIndex", {
        get: function () {
            return this.gridService.zoomIndex;
        },
        set: function (value) {
            this.gridService.zoomIndex = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HorizonComponent.prototype, "allowedLevels", {
        /**
         * The allowed levels for the horizon
         */
        get: function () {
            return detailLevels;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HorizonComponent.prototype, "weekends", {
        get: function () {
            return this.gridService.weekends;
        },
        set: function (value) {
            this.gridService.weekends = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HorizonComponent.prototype, "officeHours", {
        get: function () {
            return this.gridService.officeHours;
        },
        set: function (value) {
            this.gridService.officeHours = value;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Sets the start date to today
     */
    HorizonComponent.prototype.today = function () {
        this.date = moment();
    };
    /**
     * Go to the next period
     */
    HorizonComponent.prototype.next = function () {
        this.date = moment(this.date).add(this.level.step, this.level.duration);
    };
    /**
     * Go to the previous period
     */
    HorizonComponent.prototype.previous = function () {
        this.date = moment(this.date).subtract(this.level.step, this.level.duration);
    };
    Object.defineProperty(HorizonComponent.prototype, "displayEndDate", {
        /**
         * The end date of the current detail level
         * @readonly
         * @private
         * @type {moment.Moment}
         */
        get: function () {
            return moment(this.gridService.date).endOf(this.level.startOf);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HorizonComponent.prototype, "displayDate", {
        /**
         * Returns the date(range) for the horizon
         */
        get: function () {
            var normalized = moment.normalizeUnits(this.level.startOf);
            if (normalized === 'isoWeek') {
                normalized = 'week';
            }
            var displayStartDate = moment(this.gridService.date).startOf(this.level.startOf);
            var result = displayStartDate.format(this.localisationService.currentLanguage.formats.agenda[normalized].start);
            if (this.localisationService.currentLanguage.formats.agenda[normalized].finish) {
                result += ' - ' + this.displayEndDate.format(this.localisationService.currentLanguage.formats.agenda[normalized].finish);
            }
            return result;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Lifecycle hook that is called PlanboardRowInfoComponent is destroyed.
     */
    HorizonComponent.prototype.ngOnDestroy = function () {
        this.subscriptionPool.unsubscribeAll();
    };
    /**
     * Change zoom level down with one step
     */
    HorizonComponent.prototype.zoomIn = function () {
        this.zoomOutVisible = true;
        if (this.zoomIndex < this.availableZoom.length - 1) {
            this.zoomIndex = this.zoomIndex + 1;
            this.gridService.zoomChanged.emit(this.zoomIndex);
        }
    };
    /**
     * Change zoom level up with one step
     */
    HorizonComponent.prototype.zoomOut = function () {
        if (this.zoomIndex > 0) {
            this.zoomIndex = this.zoomIndex - 1;
            this.gridService.zoomChanged.emit(this.zoomIndex);
        }
    };
    return HorizonComponent;
}());
export { HorizonComponent };
